import { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { PieChart as PieChartIcon } from 'lucide-react';
import { Transaction } from '../../types';

interface SpendingByCategoryChartProps {
  transactions: Transaction[];
}


const CATEGORY_COLORS: Record<string, string> = {
  'Food & Dining': '#8b5cf6',
  'Transportation': '#3b82f6',
  'Shopping': '#ec4899',
  'Entertainment': '#f59e0b',
  'Bills & Utilities': '#10b981',
  'Healthcare': '#ef4444',
  'Education': '#06b6d4',
  'Salary': '#10b981',
  'Freelance': '#3b82f6',
  'Investments': '#8b5cf6',
  'Other': '#94a3b8',
};

export default function SpendingByCategoryChart({ transactions }: SpendingByCategoryChartProps) {
  // Process data for pie chart (expenses only)
  const categoryData = useMemo(() => {
    const categoryMap = new Map<string, number>();
    
    // Only count expenses
    transactions.forEach((transaction: Transaction) => {
      if (transaction.type === 'expense') {
        const current = categoryMap.get(transaction.category) || 0;
        categoryMap.set(transaction.category, current + transaction.amount);
      }
    });
    
    // Convert to array and assign colors
    return Array.from(categoryMap.entries())
      .map(([name, value]) => ({
        name,
        value: Math.abs(value), // Ensure positive values
        color: CATEGORY_COLORS[name] || '#94a3b8',
      }))
      .sort((a, b) => b.value - a.value) // Sort by value descending
      .slice(0, 8); // Show top 8 categories
  }, [transactions]);

  const totalSpending = categoryData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = totalSpending > 0 ? ((data.value / totalSpending) * 100).toFixed(1) : '0';
      
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="font-medium text-white mb-1">{data.name}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Amount:</span>
              <span className="font-medium text-white">${data.value.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Percentage:</span>
              <span className="font-medium text-gray-300">{percentage}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // If no expense data, show placeholder
  if (totalSpending === 0) {
    return (
      <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Spending by Category</h3>
            <p className="text-sm text-gray-400">Analyze your expenses</p>
          </div>
          <PieChartIcon className="w-5 h-5 text-gray-600" />
        </div>
        <div className="h-56 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <PieChartIcon className="w-12 h-12 mx-auto mb-3 text-gray-600" />
            <p className="text-gray-400">No expense data yet</p>
            <p className="text-sm text-gray-500">Add expense transactions to see categories</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-4 lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Spending by Category</h3>
          <p className="text-sm text-gray-400">Where your money goes</p>
        </div>
        <div className="flex items-center px-3 py-1 bg-blue-500/10 text-blue-400 rounded-lg text-sm">
          ${totalSpending.toLocaleString()}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        {/* Pie Chart */}
        <div className="lg:w-1/2 h-48 sm:h-56">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Category List */}
        <div className="lg:w-1/2">
          <div className="space-y-3">
            {categoryData.map((category, index) => {
              const percentage = totalSpending > 0 ? ((category.value / totalSpending) * 100).toFixed(1) : '0';
              
              return (
                <div key={index} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="text-sm text-gray-300">{category.name}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-400">{percentage}%</span>
                      <span className="font-medium text-white">${category.value.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${percentage}%`,
                        backgroundColor: category.color,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      {categoryData.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-700/50">
          <h4 className="text-sm font-medium text-gray-400 mb-3">Quick Insights</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-gray-700/30 rounded-lg p-3">
              <p className="text-xs text-gray-400">Top Category</p>
              <p className="font-medium text-white">{categoryData[0].name}</p>
              <p className="text-sm text-gray-400">
                {((categoryData[0].value / totalSpending) * 100).toFixed(1)}% of total
              </p>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-3">
              <p className="text-xs text-gray-400">Categories Tracked</p>
              <p className="font-medium text-white">{categoryData.length}</p>
              <p className="text-sm text-gray-400">Different spending areas</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}